(() => {
  const ADD_BUTTON_SELECTOR = 'button[name="add"], [data-xoa-sticky-submit]';
  const INTENT_MAX_AGE = 5000;

  class XoaCartMotion {
    constructor() {
      this.intents = [];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.captureIntent = this.captureIntent.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);

      document.addEventListener('click', this.captureIntent, true);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
        this.isSubscribed = true;
      }

    }

    captureIntent(event) {
      const button = event.target.closest?.(ADD_BUTTON_SELECTOR);
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const isOptionOpener = button.closest('modal-opener') && !button.closest('product-form');
      if (isOptionOpener) return;

      if (!event.isTrusted && this.intents.some((intent) => intent.isSticky && performance.now() - intent.time < 600)) {
        return;
      }

      const form = this.resolveForm(button);
      const variantId = form?.querySelector('[name="id"]')?.value || null;
      const sourceRect = button.getBoundingClientRect();
      if (!sourceRect.width || !sourceRect.height) return;

      this.pruneIntents();
      this.intents.push({
        button,
        form,
        variantId: variantId ? String(variantId) : null,
        quantity: this.resolveQuantity(form),
        image: this.resolveImage(button),
        sourceRect,
        isSticky: button.hasAttribute('data-xoa-sticky-submit'),
        time: performance.now(),
      });
    }

    resolveForm(button) {
      if (button.hasAttribute('data-xoa-sticky-submit')) {
        const stickyProduct = button.closest('xoa-sticky-product');
        const sectionId = stickyProduct?.dataset.section;
        if (sectionId) return document.querySelector(`#product-form-${CSS.escape(sectionId)}`);
      }

      return button.closest('form') || button.closest('product-form')?.querySelector('form') || null;
    }

    resolveQuantity(form) {
      if (!form) return 1;
      let quantityInput = form.querySelector('[name="quantity"]');
      if (!quantityInput && form.id) {
        quantityInput = document.querySelector(`[name="quantity"][form="${CSS.escape(form.id)}"]`);
      }
      const quantity = Number.parseInt(quantityInput?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    resolveImage(button) {
      const scopes = [
        button.closest('xoa-sticky-product'),
        button.closest('quick-add-modal'),
        button.closest('product-info'),
        button.closest('.product-card-wrapper'),
        button.closest('.card-wrapper'),
      ].filter(Boolean);

      const selectors = [
        '.xoa-sticky-product__image',
        '.product__media-item.is-active img',
        '.product__media img',
        '.card__media .media > img:first-child',
        '.card__media img',
      ];

      for (const scope of scopes) {
        for (const selector of selectors) {
          const image = scope.querySelector(selector);
          if (image?.currentSrc || image?.src) {
            return {
              src: image.currentSrc || image.src,
              alt: image.alt || '',
              objectPosition: getComputedStyle(image).objectPosition || '50% 50%',
            };
          }
        }
      }

      return null;
    }

    handleCartUpdate(event) {
      this.pruneIntents();
      if (!this.intents.length) return;

      const eventVariantId = event?.productVariantId ? String(event.productVariantId) : null;
      let intentIndex = eventVariantId
        ? this.intents.findIndex((intent) => intent.variantId === eventVariantId)
        : -1;
      if (intentIndex < 0) intentIndex = this.intents.length - 1;

      const [intent] = this.intents.splice(intentIndex, 1);
      if (!intent) return;

      window.requestAnimationFrame(() => this.animateToCart(intent));
    }

    pruneIntents() {
      const now = performance.now();
      this.intents = this.intents.filter((intent) => now - intent.time < INTENT_MAX_AGE);
    }

    getCartTarget() {
      const targets = [...document.querySelectorAll('[data-xoa-cart-target], #cart-icon-bubble')];
      return targets.find((target) => {
        const rect = target.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    }

    animateToCart(intent) {
      const target = this.getCartTarget();
      if (!target) return;

      const targetRect = target.getBoundingClientRect();
      const targetX = targetRect.left + targetRect.width / 2;
      const targetY = targetRect.top >= 0 ? targetRect.top + targetRect.height / 2 : 24;

      if (this.reducedMotion.matches) {
        this.showArrival(target, targetX, targetY, intent.quantity);
        return;
      }

      const size = window.innerWidth < 750 ? 58 : 68;
      const startX = intent.sourceRect.left + intent.sourceRect.width / 2;
      const startY = intent.sourceRect.top + intent.sourceRect.height / 2;
      const deltaX = targetX - startX;
      const deltaY = targetY - startY;
      const arc = Math.min(190, Math.max(76, Math.abs(deltaX) * 0.14 + 62));
      const flight = this.createFlight(intent.image, size, startX, startY);
      document.body.appendChild(flight);

      const animation = flight.animate(
        [
          { opacity: 0, transform: 'translate3d(0, 0, 0) scale(0.62) rotate(0deg)', offset: 0 },
          { opacity: 1, transform: 'translate3d(0, -10px, 0) scale(1) rotate(-2deg)', offset: 0.14 },
          {
            opacity: 1,
            transform: `translate3d(${deltaX * 0.55}px, ${deltaY * 0.55 - arc}px, 0) scale(0.72) rotate(-7deg)`,
            offset: 0.58,
          },
          {
            opacity: 0.18,
            transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.18) rotate(7deg)`,
            offset: 1,
          },
        ],
        {
          duration: 820,
          easing: 'cubic-bezier(0.2, 0.74, 0.28, 1)',
          fill: 'forwards',
        }
      );

      animation.finished
        .catch(() => {})
        .finally(() => {
          flight.remove();
          this.showArrival(target, targetX, targetY, intent.quantity);
        });
    }

    createFlight(image, size, startX, startY) {
      const flight = document.createElement('div');
      flight.className = 'xoa-cart-flight';
      flight.setAttribute('aria-hidden', 'true');
      flight.style.setProperty('--xoa-flight-size', `${size}px`);
      flight.style.left = `${startX - size / 2}px`;
      flight.style.top = `${startY - size / 2}px`;

      if (image?.src) {
        const productImage = document.createElement('img');
        productImage.src = image.src;
        productImage.alt = '';
        productImage.style.objectPosition = image.objectPosition;
        flight.appendChild(productImage);
      } else {
        const fallback = document.createElement('span');
        fallback.className = 'xoa-cart-flight__fallback';
        fallback.textContent = 'XOA';
        flight.appendChild(fallback);
      }

      return flight;
    }

    showArrival(target, targetX, targetY, quantity) {
      target.classList.remove('xoa-cart-target--pulse');
      void target.offsetWidth;
      target.classList.add('xoa-cart-target--pulse');

      const plusOne = document.createElement('span');
      plusOne.className = 'xoa-cart-plus-one';
      plusOne.setAttribute('aria-hidden', 'true');
      plusOne.textContent = `+${quantity}`;
      plusOne.style.left = `${Math.min(window.innerWidth - 20, targetX + 1)}px`;
      plusOne.style.top = `${Math.max(18, targetY - 17)}px`;
      document.body.appendChild(plusOne);

      const animation = plusOne.animate(
        [
          { opacity: 0, transform: 'translate(-50%, -30%) scale(0.55)' },
          { opacity: 1, transform: 'translate(-50%, -82%) scale(1.08)', offset: 0.28 },
          { opacity: 1, transform: 'translate(-50%, -112%) scale(1)', offset: 0.68 },
          { opacity: 0, transform: 'translate(-50%, -165%) scale(0.9)' },
        ],
        { duration: this.reducedMotion.matches ? 1 : 920, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
      );

      animation.finished
        .catch(() => {})
        .finally(() => plusOne.remove());
      window.setTimeout(() => target.classList.remove('xoa-cart-target--pulse'), 680);
    }
  }

  const start = () => {
    if (!window.xoaCartMotion) {
      window.xoaCartMotion = new XoaCartMotion();
      document.documentElement.setAttribute(
        'data-xoa-cart-motion',
        window.xoaCartMotion.isSubscribed ? 'ready' : 'unavailable'
      );
    }
  };

  start();
})();
